import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../config/api';
import toast from 'react-hot-toast';
import { Calendar, Hourglass, Plus, X ,ArrowUpRight} from 'lucide-react';
import { masterPageShell, masterHeader, masterHeaderCard, masterHeaderIcon, masterTitle } from '../components/masterPageStyles';

const toLocalYmd = (dateValue) => {
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const AppointmentManagement = () => {
  
  const navigate = useNavigate();
  const [prescriptions, setPrescriptions] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [savedTimes, setSavedTimes] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => toLocalYmd(new Date()).slice(0, 7));
  const [loadingId, setLoadingId] = useState(null);
  const [closingSavedTimeKey, setClosingSavedTimeKey] = useState('');
const [rowInputs, setRowInputs] = useState({});
const [submittedRxMap, setSubmittedRxMap] = useState({});
const [cancelingId, setCancelingId] = useState(null);
const [loading, setLoading] = useState(false);


  const loadData = async () => {
    setLoading(true);
    try {
      const [appointmentRes, savedTimeRes,rxRes] = await Promise.all([
        api.get(`/appointments/all`),
        api.get(`/appointments/saved-time-summary`),
        api.get(`/prescriptions/all`)
      ]);
      if (appointmentRes.data.success) setAppointments(appointmentRes.data.data || []);
      if (savedTimeRes.data.success) setSavedTimes(savedTimeRes.data.data || []);
      if (rxRes.data.success) setPrescriptions(rxRes.data.data || []);
    } catch (err) { toast.error('FAILED TO LOAD DATA'); }
     finally {
      setLoading(false); // 👈 2. ADD THIS: Turns loading OFF when finished (success or error)
    }
  };

  useEffect(() => { loadData(); }, []);
// Track row inputs for inline fields

const handleRowInputChange = (apptId, field, value) => {
  setRowInputs(prev => ({
    ...prev,
    [apptId]: {
      ...(prev[apptId] || { nextVisitDate: '', labTest: false, files: [], description: '' }),
      [field]: value
    }
  }));
};

const handleRowFileChange = async (apptId, e) => {
  const selectedFiles = Array.from(e.target.files || []);
  if (selectedFiles.length === 0) return;

  try {
    const filePromises = selectedFiles.map((file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve({ name: file.name, data: reader.result });
        reader.onerror = (error) => reject(error);
      });
    });

    const convertedFiles = await Promise.all(filePromises);
    const current = rowInputs[apptId] || { nextVisitDate: '', labTest: false, files: [], description: '' };
    
    setRowInputs(prev => ({
      ...prev,
      [apptId]: { ...current, files: [...(current.files || []), ...convertedFiles] }
    }));
    toast.success(`${convertedFiles.length} file(s) attached`);
  } catch (err) {
    toast.error('Failed to parse file');
  }
};

const submitPrescriptionOnly = async (appt) => {
    const inputs = rowInputs[appt._id] || { nextVisitDate: '', labTest: false, files: [], description: '' };
    
    if (!inputs.files.length && !inputs.nextVisitDate && !inputs.labTest) {
      toast.error('Please add a file, follow-up date, or select lab test first.');
      return;
    }

    try {
      await api.post('/prescriptions/add', {
        doctor: appt.doctorId,
        patient: appt.patientId || appt._id,
        patientName: appt.patientName || '',
        appointmentId: appt.appointmentId || '',
        patientPhone: appt.patientMobile || '',
        visitDate: appt.appointmentDate,
        type: inputs.files.length > 0 ? 'Handwritten' : 'Digital',
        prescriptionId: `PR-${Date.now().toString().slice(-6)}`,
        files: inputs.files,
        description: inputs.description || 'Recorded from Console',
        nextVisitDate: inputs.nextVisitDate,
        labTest: inputs.labTest,
        medicines: []
      });

      // Mark this specific appointment as submitted
      setSubmittedRxMap(prev => ({ ...prev, [appt._id]: true }));
      toast.success('PRESCRIPTION DETAILS SAVED TO MODEL!');
    } catch (err) {
      toast.error('FAILED TO SAVE PRESCRIPTION');
    }
  };

  const cancelAppointment = async (appt) => {
  if (cancelingId) return;
  if (!window.confirm(`Are you sure you want to cancel appointment for ${appt.patientName}?`)) return;
  
  setCancelingId(appt._id);
  setAppointments(prev => prev.map(a => 
    a._id === appt._id ? { ...a, status: 'Canceled' } : a
  ));

  try {
    await api.put(`/appointments/update/${appt._id}`, { 
      status: 'Canceled',
      doctorId: appt.doctorId,
      appointmentDate: appt.appointmentDate
    });
    toast.success('APPOINTMENT CANCELED SUCCESSFULLY');
    await loadData();
  } catch (err) {
    loadData();
    toast.error('FAILED TO CANCEL APPOINTMENT');
  } finally {
    setCancelingId(null);
  }
};

const markAsChecked = async (appt) => {
  if (loadingId) return;
  if (!window.confirm(`Are you sure you want to mark appointment for ${appt.patientName} as Checked Up (Done)?`)) {
    return;
  }
  setLoadingId(appt._id);

  const inputs = rowInputs[appt._id] || { nextVisitDate: '', labTest: false, files: [], description: '' };

  let savedMinutes = 0;
  if (!appt.isExtraWalkIn && appt.selectedSlot && appt.selectedSlot.includes('-')) {
    const parts = appt.selectedSlot.split('-');
    const startTimeStr = parts[0].trim();
    const endTimeStr = parts[parts.length - 1].trim(); 

    const parse12HrToMinutes = (tStr) => {
      const clean = tStr.toUpperCase().trim();
      const isPM = clean.includes('PM');
      const isAM = clean.includes('AM');
      const timePart = clean.replace(/(AM|PM)/g, '').replace('.', ':').trim();
      let [h, m] = timePart.split(':').map(Number);
      if (isNaN(h)) h = 0;
      if (isNaN(m)) m = 0;
      if (isPM && h < 12) h += 12;
      if (isAM && h === 12) h = 0;
      return h * 60 + m;
    };

    const startTotal = parse12HrToMinutes(startTimeStr);
    const endTotal = parse12HrToMinutes(endTimeStr);
    const slotDuration = endTotal - startTotal;

    const now = new Date();
    const currentTotal = now.getHours() * 60 + now.getMinutes();

    if (currentTotal < endTotal) {
      savedMinutes = endTotal - currentTotal;
    } else {
      savedMinutes = slotDuration > 0 ? slotDuration : 15;
    }
  }

  // Optimistic Update
  setAppointments(prev => prev.map(a => 
    a._id === appt._id ? { ...a, status: 'CheckedUp', savedMinutes } : a
  ));

  try {
    // 1. Update Appointment Status
    await api.put(`/appointments/update/${appt._id}`, { 
      status: 'CheckedUp',
      savedMinutes: savedMinutes,
      doctorId: appt.doctorId,
      appointmentDate: appt.appointmentDate
    });

    // 2. Save Prescription Data directly to the Prescription Model DB
    await api.post('/prescriptions/add', {
      doctor: appt.doctorId,
      patient: appt.patientId || appt._id,
      patientPhone: appt.patientMobile || '',
      visitDate: appt.appointmentDate,
      type: inputs.files.length > 0 ? 'Handwritten' : 'Digital',
      prescriptionId: `PR-${Date.now().toString().slice(-6)}`,
      files: inputs.files,
      description: inputs.description || 'Recorded from Console',
      nextVisitDate: inputs.nextVisitDate,
      labTest: inputs.labTest,
      medicines: []
    });

    toast.success(`CHECKED UP & RECORDED! ${savedMinutes > 0 ? savedMinutes + ' MIN SAVED' : ''}`);
    await loadData(); 
  } catch (err) {
    loadData();
    toast.error('FAILED TO UPDATE STATUS OR SAVE PRESCRIPTION');
  } finally {
    setLoadingId(null);
  }
};
 
const dateGroups = useMemo(() => {
  const groups = {};
  appointments.forEach(appt => {
    // Exclude both CheckedUp and Canceled
    if (appt.appointmentDate && appt.status !== 'CheckedUp' && appt.status !== 'Canceled') {
      if (!groups[appt.appointmentDate]) groups[appt.appointmentDate] = [];
      groups[appt.appointmentDate].push(appt);
    }
  });
  return groups;
}, [appointments]);


  const todayYmd = useMemo(() => toLocalYmd(new Date()), []);

  const savedTimeByDateDoctor = useMemo(() => {
    const map = {};
    savedTimes.forEach((item) => {
      if (!item.date || !item.doctorId) return;
      const key = `${item.date}|${item.doctorId}`;
      map[key] = item;
    });
    return map;
  }, [savedTimes]);

  const allDates = useMemo(() => {
    const dates = new Set(Object.keys(dateGroups));
    savedTimes.forEach((item) => {
      if (item.date && item.totalSavedMinutes > 0) dates.add(item.date);
    });
    return [...dates]
      .filter((date) => date >= todayYmd && date.startsWith(selectedMonth))
      .sort();
  }, [dateGroups, savedTimes, selectedMonth, todayYmd]);

  useEffect(() => {
    if (!selectedDate && allDates.length > 0) {
      setSelectedDate(allDates[0]);
    } else if (selectedDate && !allDates.includes(selectedDate)) {
      setSelectedDate(allDates[0] || null);
    }
  }, [allDates, selectedDate]);

  const selectedDoctorGroups = useMemo(() => {
    if (!selectedDate) return {};
    const groups = {};

    (dateGroups[selectedDate] || []).forEach((appt) => {
      const dId = appt.doctorId || 'UNASSIGNED';
      if (!groups[dId]) {
        groups[dId] = {
          name: appt.doctorName || dId,
          department: appt.department || '',
          list: [],
          savedMinutes: 0,
          doctorWindow: ''
        };
      }
      groups[dId].list.push(appt);
    });

    savedTimes
      .filter((item) => item.date === selectedDate)
      .forEach((item) => {
        const dId = item.doctorId || 'UNASSIGNED';
        if (!groups[dId]) {
          groups[dId] = {
            name: item.doctorName || dId,
            department: item.department || '',
            list: [],
            savedMinutes: 0,
            doctorWindow: ''
          };
        }
        groups[dId].name = groups[dId].name || item.doctorName || dId;
        groups[dId].department = groups[dId].department || item.department || '';
        groups[dId].savedMinutes = Number(item.totalSavedMinutes || 0);
        groups[dId].doctorWindow = item.doctorWindow || '';
      });

    return groups;
  }, [dateGroups, savedTimes, selectedDate]);

  const openExtraWalkInBooking = (doctorId, data) => {
    if (!selectedDate || !doctorId || doctorId === 'UNASSIGNED') return;
    navigate('/AppointmentBooking', {
      state: {
        extraWalkInBooking: {
          department: data.department || '',
          doctorId,
          doctorName: data.name || '',
          appointmentDate: selectedDate,
          bookingType: 'Walk-in',
          savedMinutes: data.savedMinutes || 0,
          doctorWindow: data.doctorWindow || ''
        }
      }
    });
  };

  const closeSavedTime = async (doctorId, data) => {
    if (!selectedDate || !doctorId || doctorId === 'UNASSIGNED') return;
    const confirmed = window.confirm(`Close saved time for DR. ${data.name} on ${selectedDate}?`);
    if (!confirmed) return;

    const key = `${selectedDate}|${doctorId}`;
    setClosingSavedTimeKey(key);
    setSavedTimes((prev) => prev.filter((item) => !(item.date === selectedDate && item.doctorId === doctorId)));

    try {
      const res = await api.patch('/appointments/saved-time/close', {
        doctorId,
        date: selectedDate
      });

      if (res.data.success) {
        toast.success('SAVED TIME CLOSED');
        await loadData();
      } else {
        throw new Error();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'FAILED TO CLOSE SAVED TIME');
      await loadData();
    } finally {
      setClosingSavedTimeKey('');
    }
  };

  return (
    <div className={masterPageShell}>
     <header className={masterHeader}>
  <div className={`${masterHeaderCard} flex items-center justify-between`}>
    <div className="flex items-center gap-3">
      <div className={masterHeaderIcon}><Calendar size={20} /></div>
      <h1 className={masterTitle}>DOCTOR-WISE CLINICAL</h1>
    </div>
    
    {/* Go to Prescriptions Button on the Right */}
    <button 
      type="button"
      onClick={() => navigate('/Prescription')}
      className="px-3 py-1.5 text-[10px] font-black text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg shadow-sm flex items-center gap-1.5 transition"
    >
      <ArrowUpRight size={14} /> GO TO PRESCRIPTION
    </button>
  </div>
</header>

      <main className="p-4 grid grid-cols-1 lg:grid-cols-[140px_1fr] gap-1">
        <div className="space-y-2">
          <div className="rounded-lg border border-slate-200 bg-white p-2">
            <label className="mb-1 block text-[9px] font-black uppercase tracking-wider text-emerald-600">
              Month
            </label>
            <input
              type="month"
              value={selectedMonth}
              min={todayYmd.slice(0, 7)}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-full rounded border border-slate-200 px-2 py-1 text-[11px] font-bold outline-none"
            />
          </div>
          {allDates.map(date => (
            <button key={date} onClick={() => setSelectedDate(date)} 
              className={`w-full p-1 rounded-lg border text-left transition-all ${selectedDate === date ? 'bg-emerald-600 text-white shadow-md' : 'bg-white border-slate-200 hover:border-slate-300'}`}>
              <div className="text-[10px] font-black uppercase tracking-wider">{date}</div>
              <div className={`text-[9px] font-bold ${selectedDate === date ? 'text-emerald-100' : 'text-slate-400'}`}>
                {(dateGroups[date] || []).filter(a => a.status !== 'CheckedUp').length} ACTIVE | {savedTimes.filter((item) => item.date === date).reduce((sum, item) => sum + Number(item.totalSavedMinutes || 0), 0)} MIN SAVED
              </div>
            </button>
          ))}
          {allDates.length === 0 && (
            <div className="rounded-lg border border-dashed border-slate-200 bg-white p-4 text-center text-[10px] font-black uppercase text-slate-300">
              No valid dates for this month
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {selectedDate ? (
            <div className="p-2 space-y-4">
            {Object.entries(selectedDoctorGroups).map(([dId, data]) => {
  const activeList = data.list.filter(a => a.status !== 'CheckedUp' && a.status !== 'Canceled');
                const savedRecord = savedTimeByDateDoctor[`${selectedDate}|${dId}`];
                const savedMinutes = Number(savedRecord?.totalSavedMinutes ?? data.savedMinutes ?? 0);

                return (
                  <div key={dId} className="border border-slate-200 rounded-lg overflow-hidden">
                    <div className="bg-slate-800 text-emerald-400 px-3 py-1 text-[10px] font-black uppercase tracking-widest flex flex-col gap-2 md:flex-row md:justify-between md:items-center">
                      <div>
                        <span>DR. {data.name}</span>
                        {data.doctorWindow && <span className="ml-2 text-[9px] text-slate-300">{data.doctorWindow}</span>}
                      </div>
                      <div className="flex flex-wrap items-center gap-3">
                         <span className="flex items-center gap-1"><Hourglass size={10} /> TOTAL SAVED: {savedMinutes} MIN</span>
                         <span>{activeList.length} ACTIVE</span>
                         {savedMinutes > 0 && (
                          <>
                            <button
                              type="button"
                              onClick={() => openExtraWalkInBooking(dId, data)}
                              className="inline-flex items-center gap-1 rounded bg-emerald-500 px-2 py-1 text-[8px] font-black text-white hover:bg-emerald-600"
                            >
                              <Plus size={10} /> BOOK WALK-IN
                            </button>
                            <button
                              type="button"
                              disabled={closingSavedTimeKey === `${selectedDate}|${dId}`}
                              onClick={() => closeSavedTime(dId, data)}
                              className="inline-flex items-center gap-1 rounded bg-rose-500 px-2 py-1 text-[8px] font-black text-white hover:bg-rose-600 disabled:bg-slate-300"
                            >
                              <X size={10} /> {closingSavedTimeKey === `${selectedDate}|${dId}` ? 'CLOSING' : 'CLOSE'}
                            </button>
                          </>
                         )}
                      </div>
                    </div>
                  <div className="w-full overflow-x-auto">
                <table className="w-full min-w-[750px] text-[9px] font-bold text-slate-700 uppercase border-collapse">
                  <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-1 py-1 text-left">TIME</th>
                        <th className="px-1 py-1 text-left">PATIENT</th>
                        <th className="px-1 py-1 text-left">DEPT</th>
                        <th className="px-1 py-1 text-left">PAYMENT</th>
                        <th className="px-1 py-1 text-left">UPLOAD</th>
                        <th className="px-1 py-1 text-left">FOLLOW-UP</th>
                        <th className="px-1 py-1 text-center">LAB</th>
                        <th className="px-1 py-1 text-right">ACTION</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="px-3 py-6 text-center text-[10px] font-black uppercase text-slate-500">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-3 h-3 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
                          <span>Please wait ......</span>
                        </div>
                      </td>
                    </tr>
                  ) : activeList.length > 0 ? activeList.sort((a,b) => (a.tokenNumber || 0) - (b.tokenNumber || 0)).map(appt => {
                    const rowVal = rowInputs[appt._id] || { nextVisitDate: '', labTest: false, files: [] };
                    return (
                      <tr key={appt._id} className="hover:bg-emerald-50/50 text-[10px]">
                        <td className="px-1 py-0.5 text-emerald-700">
                          {appt.selectedSlot || 'WALK-IN'}
                          {appt.isExtraWalkIn && (
                            <span className="ml-1 rounded bg-blue-100 px-1 py-0.5 text-[8px] font-black uppercase text-blue-700">
                              Extra
                            </span>
                          )}
                        </td>
                        <td className="px-1 py-0.5 text-slate-900 truncate max-w-[90px]">{appt.patientName}</td>
                        <td className="px-1 py-0.5 text-slate-600 truncate max-w-[80px]">{appt.department}</td>
                        <td className="px-1 py-0.5">
                          <span className={`px-1 py-0 rounded ${appt.paymentStatus === 'Paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                            {appt.paymentStatus}
                          </span>
                        </td>

                        {/* Inline Prescription Upload Column */}
                      <td className="px-1 py-0.5 align-middle">
                          <div className="flex flex-col items-start gap-0.5">
                            {/* Custom Styled Upload Button */}
                            <label className="cursor-pointer inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-[9px] font-black uppercase tracking-wider shadow-sm transition">
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                              </svg>
                              <span>Upload</span>
                              <input 
                                type="file" 
                                accept="image/*,application/pdf"
                                multiple
                                onChange={(e) => handleRowFileChange(appt._id, e)}
                                className="hidden" 
                              />
                            </label>

                            {/* Attachment Status indicator */}
                            {rowVal.files?.length > 0 ? (
                              <span className="text-[9px] font-extrabold text-emerald-700 bg-emerald-100 px-1 rounded">
                                ✓ {rowVal.files.length} file(s)
                              </span>
                            ) : (
                              <span className="text-[8px] text-slate-400">No file</span>
                            )}
                          </div>
                        </td>

                        {/* Inline Follow-up Date Column */}
                        <td className="px-1 py-0.5">
                          <input 
                            type="date"
                            value={rowVal.nextVisitDate}
                            onChange={(e) => handleRowInputChange(appt._id, 'nextVisitDate', e.target.value)}
                            className="border border-slate-200 rounded px-1 py-0.5 text-[9px] font-bold text-slate-700 outline-none w-24"
                          />
                        </td>

                        {/* Inline Lab Test Toggle Column */}
                        <td className="px-1 py-0.5 text-center">
                          <input 
                            type="checkbox"
                            checked={!!rowVal.labTest}
                            onChange={(e) => handleRowInputChange(appt._id, 'labTest', e.target.checked)}
                            className="w-3.5 h-3.5 text-emerald-600 rounded border-slate-300 cursor-pointer"
                          />
                        </td>

                      <td className="px-1 py-0.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button 
                            type="button"
                            disabled={submittedRxMap[appt._id]}
                            onClick={() => submitPrescriptionOnly(appt)}
                            className={`px-2 py-0.5 rounded text-[8px] font-black shadow-sm ${
                              submittedRxMap[appt._id] 
                                ? 'bg-slate-300 text-slate-500 cursor-not-allowed' 
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                          >
                            {submittedRxMap[appt._id] ? 'SUBMITTED' : 'SUBMIT'}
                          </button>
                          
                          <button 
                            disabled={loadingId === appt._id}
                            onClick={() => markAsChecked(appt)}
                            className={`px-2 py-0.5 rounded text-[8px] font-black shadow-sm ${loadingId === appt._id ? 'bg-slate-300' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}
                          >
                            {loadingId === appt._id ? '...' : 'DONE'}
                          </button>

                          {/* CANCEL BUTTON */}
                          <button 
                            disabled={cancelingId === appt._id}
                            onClick={() => cancelAppointment(appt)}
                            className={`px-2 py-0.5 rounded text-[8px] font-black shadow-sm ${cancelingId === appt._id ? 'bg-slate-300' : 'bg-rose-600 text-white hover:bg-rose-700'}`}
                          >
                            {cancelingId === appt._id ? '...' : 'CANCEL'}
                          </button>
                        </div>
                      </td>
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan="8" className="px-3 py-4 text-center text-[10px] font-black uppercase text-slate-300">
                        No active scheduled patients. Use saved time for walk-in booking.
                      </td>
                    </tr>
                  )}
                </tbody>
                  </table>
                  </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-[10px] font-black text-slate-300 uppercase">Select a date</div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AppointmentManagement;
